import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { RefreshCw, Users, UserPlus, TrendingUp, DollarSign, MousePointer, ShoppingCart, Copy, Mail, Eye, X, FlaskConical } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
interface Affiliate {
  id: string;
  name: string;
  email: string;
  referral_code: string;
  commission_rate: number;
  total_earnings: number;
  pending_earnings: number;
  is_active: boolean;
  created_at: string;
}

interface AffiliateStats {
  affiliate_id: string;
  clicks: number;
  tests: number;
  conversions: number;
  pending_conversions: number;
  approved_conversions: number;
  total_revenue: number;
}

interface AffiliateEmail {
  email: string;
  type: string;
  created_at: string;
  status: string;
}

const AdminAffiliatesSection = () => {
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [stats, setStats] = useState<Map<string, AffiliateStats>>(new Map());
  const [loading, setLoading] = useState(true);
  const [emailsDialogOpen, setEmailsDialogOpen] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState<Affiliate | null>(null);
  const [affiliateEmails, setAffiliateEmails] = useState<AffiliateEmail[]>([]);
  const [loadingEmails, setLoadingEmails] = useState(false);

  useEffect(() => {
    fetchAffiliatesData();
  }, []);

  const fetchAffiliatesData = async () => {
    setLoading(true);
    try {
      // Fetch all affiliates
      const { data: affiliatesData, error: affiliatesError } = await supabase
        .from("affiliates")
        .select("*")
        .order("created_at", { ascending: false });

      if (affiliatesError) throw affiliatesError;
      setAffiliates((affiliatesData as Affiliate[]) || []);

      // Fetch clicks per affiliate
      const { data: clicksData, error: clicksError } = await supabase
        .from("referral_clicks")
        .select("affiliate_id");

      if (clicksError) throw clicksError;

      // Fetch conversions per affiliate
      const { data: conversionsData, error: conversionsError } = await supabase
        .from("conversions")
        .select("*");

      if (conversionsError) throw conversionsError;

      // Fetch test requests per affiliate (via referral_code)
      const { data: testsData, error: testsError } = await supabase
        .from("requests")
        .select("referral_code")
        .eq("type", "test_24h")
        .not("referral_code", "is", null);

      if (testsError) throw testsError;

      // Calculate stats per affiliate
      const statsMap = new Map<string, AffiliateStats>();
      
      affiliatesData?.forEach((affiliate: Affiliate) => {
        const affiliateClicks = clicksData?.filter(c => c.affiliate_id === affiliate.id) || [];
        const affiliateConversions = conversionsData?.filter(c => c.affiliate_id === affiliate.id) || [];
        const affiliateTests = testsData?.filter(t => t.referral_code === affiliate.referral_code) || [];
        
        statsMap.set(affiliate.id, {
          affiliate_id: affiliate.id,
          clicks: affiliateClicks.length,
          tests: affiliateTests.length,
          conversions: affiliateConversions.length,
          pending_conversions: affiliateConversions.filter(c => c.status === "pending").length,
          approved_conversions: affiliateConversions.filter(c => c.status === "approved").length,
          total_revenue: affiliateConversions
            .filter(c => c.status === "approved")
            .reduce((sum, c) => sum + (c.order_amount || 0), 0),
        });
      });

      setStats(statsMap);
    } catch (error) {
      console.error("Error fetching affiliates:", error);
      toast.error("Erreur lors du chargement des affiliés");
    } finally {
      setLoading(false);
    }
  };

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    const totalAffiliates = affiliates.length;
    const activeAffiliates = affiliates.filter(a => a.is_active).length;
    const newAffiliatesThisMonth = affiliates.filter(a => 
      new Date(a.created_at) >= thirtyDaysAgo
    ).length;
    const newAffiliatesThisWeek = affiliates.filter(a => 
      new Date(a.created_at) >= sevenDaysAgo
    ).length;

    let totalClicks = 0;
    let totalConversions = 0;
    let totalRevenue = 0;

    stats.forEach((s) => {
      totalClicks += s.clicks;
      totalConversions += s.conversions;
      totalRevenue += s.total_revenue;
    });

    const conversionRate = totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0;

    return {
      totalAffiliates,
      activeAffiliates,
      newAffiliatesThisMonth,
      newAffiliatesThisWeek,
      totalClicks,
      totalConversions,
      totalRevenue,
      conversionRate,
    };
  }, [affiliates, stats]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copié !");
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const fetchAffiliateEmails = async (affiliate: Affiliate) => {
    setSelectedAffiliate(affiliate);
    setEmailsDialogOpen(true);
    setLoadingEmails(true);
    
    try {
      const { data, error } = await supabase
        .from("requests")
        .select("email, type, created_at, status")
        .eq("referral_code", affiliate.referral_code)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setAffiliateEmails(data || []);
    } catch (error) {
      console.error("Error fetching affiliate emails:", error);
      toast.error("Erreur lors du chargement des emails");
    } finally {
      setLoadingEmails(false);
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "test_24h": return "Test 24h";
      case "3months": return "3 mois";
      case "6months": return "6 mois";
      case "12months": return "12 mois";
      default: return type;
    }
  };

  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case "test_24h": return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "3months": return "bg-green-500/20 text-green-400 border-green-500/30";
      case "6months": return "bg-amber-500/20 text-amber-400 border-amber-500/30";
      case "12months": return "bg-purple-500/20 text-purple-400 border-purple-500/30";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const getActivityBadge = (affiliateId: string) => {
    const affiliateStats = stats.get(affiliateId);
    if (!affiliateStats) return null;

    const { clicks, conversions } = affiliateStats;

    if (conversions > 0) {
      return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Performant</Badge>;
    } else if (clicks > 10) {
      return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Actif</Badge>;
    } else if (clicks > 0) {
      return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">En attente</Badge>;
    } else {
      return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Inactif</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-primary" />
              <p className="text-muted-foreground text-sm">Total Affiliés</p>
            </div>
            <p className="text-3xl font-bold text-foreground">{summaryStats.totalAffiliates}</p>
            <p className="text-xs text-muted-foreground">{summaryStats.activeAffiliates} actifs</p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <UserPlus className="w-4 h-4 text-green-400" />
              <p className="text-muted-foreground text-sm">Nouveaux (30j)</p>
            </div>
            <p className="text-3xl font-bold text-green-400">{summaryStats.newAffiliatesThisMonth}</p>
            <p className="text-xs text-muted-foreground">{summaryStats.newAffiliatesThisWeek} cette semaine</p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <MousePointer className="w-4 h-4 text-blue-400" />
              <p className="text-muted-foreground text-sm">Total Clics</p>
            </div>
            <p className="text-3xl font-bold text-blue-400">{summaryStats.totalClicks}</p>
            <p className="text-xs text-muted-foreground">{summaryStats.conversionRate.toFixed(1)}% taux conv.</p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-amber-400" />
              <p className="text-muted-foreground text-sm">Revenus générés</p>
            </div>
            <p className="text-3xl font-bold text-amber-400">{formatCurrency(summaryStats.totalRevenue)}</p>
            <p className="text-xs text-muted-foreground">{summaryStats.totalConversions} conversions</p>
          </CardContent>
        </Card>
      </div>

      {/* Affiliates Table */}
      <Card className="bg-card border-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-foreground flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Performance des Affiliés
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAffiliatesData}
            className="border-border"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Chargement...</div>
          ) : affiliates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Aucun affilié inscrit
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="text-muted-foreground">Affilié</TableHead>
                    <TableHead className="text-muted-foreground">Code</TableHead>
                    <TableHead className="text-muted-foreground text-center">Clics</TableHead>
                    <TableHead className="text-muted-foreground text-center">Tests</TableHead>
                    <TableHead className="text-muted-foreground text-center">Conversions</TableHead>
                    <TableHead className="text-muted-foreground text-center">Taux</TableHead>
                    <TableHead className="text-muted-foreground text-right">Revenus</TableHead>
                    <TableHead className="text-muted-foreground text-center">Statut</TableHead>
                    <TableHead className="text-muted-foreground">Inscrit le</TableHead>
                    <TableHead className="text-muted-foreground">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {affiliates.map((affiliate) => {
                    const affiliateStats = stats.get(affiliate.id);
                    const clicks = affiliateStats?.clicks || 0;
                    const tests = affiliateStats?.tests || 0;
                    const conversions = affiliateStats?.conversions || 0;
                    const convRate = clicks > 0 ? ((conversions / clicks) * 100).toFixed(1) : "0.0";
                    const revenue = affiliateStats?.total_revenue || 0;

                    return (
                      <TableRow key={affiliate.id} className="border-border hover:bg-muted/50">
                        <TableCell>
                          <div>
                            <p className="font-medium text-foreground">{affiliate.name}</p>
                            <p className="text-sm text-muted-foreground">{affiliate.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <code className="bg-muted px-2 py-1 rounded text-sm">{affiliate.referral_code}</code>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => copyToClipboard(affiliate.referral_code)}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={clicks > 0 ? "text-blue-400 font-medium" : "text-muted-foreground"}>
                            {clicks}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <FlaskConical className="w-3 h-3 text-muted-foreground" />
                            <span className={tests > 0 ? "text-purple-400 font-medium" : "text-muted-foreground"}>
                              {tests}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <ShoppingCart className="w-3 h-3 text-muted-foreground" />
                            <span className={conversions > 0 ? "text-green-400 font-medium" : "text-muted-foreground"}>
                              {conversions}
                            </span>
                            {affiliateStats && affiliateStats.pending_conversions > 0 && (
                              <Badge variant="outline" className="ml-1 text-xs">
                                {affiliateStats.pending_conversions} en attente
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={parseFloat(convRate) > 0 ? "text-primary font-medium" : "text-muted-foreground"}>
                            {convRate}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={revenue > 0 ? "text-amber-400 font-medium" : "text-muted-foreground"}>
                            {formatCurrency(revenue)}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          {getActivityBadge(affiliate.id)}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDate(affiliate.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => fetchAffiliateEmails(affiliate)}
                              title="Voir les emails"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => {
                                window.location.href = `mailto:${affiliate.email}?subject=Votre compte affilié Roi du Streaming`;
                              }}
                              title="Contacter"
                            >
                              <Mail className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog pour voir les emails de l'affilié */}
      <Dialog open={emailsDialogOpen} onOpenChange={setEmailsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Emails capturés via {selectedAffiliate?.name}
              <Badge variant="outline" className="ml-2">
                {selectedAffiliate?.referral_code}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            {loadingEmails ? (
              <div className="text-center py-8 text-muted-foreground">Chargement...</div>
            ) : affiliateEmails.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Aucun email capturé via ce code affilié
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-4 p-3 bg-muted/50 rounded-lg">
                  <span className="text-sm text-muted-foreground">
                    {affiliateEmails.length} email{affiliateEmails.length > 1 ? 's' : ''} capturé{affiliateEmails.length > 1 ? 's' : ''}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const emails = affiliateEmails.map(e => e.email).join(', ');
                      navigator.clipboard.writeText(emails);
                      toast.success("Emails copiés !");
                    }}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copier tous
                  </Button>
                </div>
                
                <Table>
                  <TableHeader>
                    <TableRow className="border-border">
                      <TableHead className="text-muted-foreground">Email</TableHead>
                      <TableHead className="text-muted-foreground">Type</TableHead>
                      <TableHead className="text-muted-foreground">Date</TableHead>
                      <TableHead className="text-muted-foreground">Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {affiliateEmails.map((emailData, index) => (
                      <TableRow key={index} className="border-border">
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-foreground">{emailData.email}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => copyToClipboard(emailData.email)}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getTypeBadgeColor(emailData.type)}>
                            {getTypeLabel(emailData.type)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDate(emailData.created_at)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={emailData.status === "credentials_sent" ? "default" : "outline"}>
                            {emailData.status === "credentials_sent" ? "Envoyé" : "Nouveau"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAffiliatesSection;
